// Lightweight auth service for communicating with backend auth endpoints
import { PublicClientApplication } from '@azure/msal-browser';
import { tokenRequest } from '../config/authConfig';

// MSAL instance for token acquisition
let msalInstance: PublicClientApplication | null = null;

/**
 * Initialize MSAL instance for token acquisition
 * Should be called once at app startup
 */
export function initializeMsal(instance: PublicClientApplication) {
  msalInstance = instance;
}

/**
 * Get Entra External ID access token with retry logic
 *
 * Acquires an access token silently if possible, otherwise prompts for interaction.
 * This token is used for authenticating API requests to the backend.
 * Implements retry logic to handle race conditions during initial login.
 *
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param retryDelayMs - Delay between retries in milliseconds (default: 500)
 * @returns Promise<string | null> - Access token or null if not authenticated
 */
export async function getEntraAccessToken(
  maxRetries: number = 3,
  retryDelayMs: number = 500
): Promise<string | null> {
  if (!msalInstance) {
    console.warn('MSAL instance not initialized');
    return null;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null;
  }

  const request = {
    ...tokenRequest,
    account: accounts[0],
  };

  // Retry loop to handle race conditions during initial login
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Try to acquire token silently
      // Use idToken (aud = CLIENT_ID) instead of accessToken (aud = MS Graph)
      const response = await msalInstance.acquireTokenSilent(request);
      if (response.idToken) {
        return response.idToken;
      }
    } catch (error) {
      // If this is the last attempt, try interactive popup
      if (attempt === maxRetries) {
        console.warn('Silent token acquisition failed, trying popup:', error);
        try {
          // Fall back to interactive popup
          const response = await msalInstance.acquireTokenPopup(request);
          return response.idToken;
        } catch (popupError) {
          console.error('Token acquisition failed:', popupError);
          return null;
        }
      }

      // Not the last attempt - wait and retry
      console.debug(`Token acquisition attempt ${attempt}/${maxRetries} failed, retrying...`);
      await new Promise(resolve => setTimeout(resolve, retryDelayMs));
    }
  }

  return null;
}

/**
 * Wait for token to be available
 *
 * Waits until a valid access token can be acquired or timeout is reached.
 * Useful for ensuring token is ready before making API calls.
 *
 * @param timeoutMs - Maximum time to wait in milliseconds (default: 5000)
 * @returns Promise<string | null> - Access token or null if timeout
 */
export async function waitForToken(timeoutMs: number = 5000): Promise<string | null> {
  const startTime = Date.now();
  const checkInterval = 200; // Check every 200ms

  while (Date.now() - startTime < timeoutMs) {
    const token = await getEntraAccessToken(1, 0); // Single attempt, no delay
    if (token) {
      return token;
    }
    // Wait before next check
    await new Promise(resolve => setTimeout(resolve, checkInterval));
  }

  console.warn('Token wait timeout reached');
  return null;
}

// Legacy signin/signup removed - use Entra External ID only

// Legacy token validation removed - Entra External ID tokens are validated by MSAL automatically

// Legacy token refresh removed - Entra External ID tokens are refreshed by MSAL automatically using acquireTokenSilent()
